import { useSkeem } from "../providers/SkeemProvider"

import { useState, useEffect, useRef } from "react"
import { useInterval } from "./useInterval"
import { sub } from "date-fns/esm"
import { Cache } from "../utils/cache"
import { LOCK_VALID_FOR, LOCK_REFRESH_TIME, LOCK_CHECK_TIME } from "../constants/lockTimes"
import { errorIs } from "@cd2/error-box"

/**
 * Locks prevent editing (softly) to given resources.
 *
 * A user claims a lock on a resource by calling the useLock(resource, id) hook. This will create a
 * lock record for the specified resource/id and user. The lock has a lockedAt date specifying when
 * the lock was claimed. When the hook unmounts the lock is released.
 *
 * To check what locks are on a resource the useLockData(resource) hook is used. This hook will
 * fetch the data about what locks are held for the given resource. If a locks lockedAt date is too
 * old then the data is ignored.
 */

/**
 * claim a lock for a specified resource and id
 * this lock will last for LOCK_VALID_FOR and be refreshed every LOCK_REFRESH_TIME
 * when the hook unmounts the lock is released immediately
 *
 * yields: canLock boolean which is true if you can lock a record and have locked it
 * yields: reset which sets canLock to true which is akin to mounting the hook for the first time
 *
 * @param resource model_name
 * @param id model_id
 */
export function useLock(resource: string, id?: string) {
  const skeem = useSkeem()

  const [canLock, setCanLock] = useState(true)

  // we store a reference to the obtained lock, that way we can delete it immediately on unmount
  // or resource change. It also means we update the lock time when refreshing instead of creating
  // a new record
  const lock = useRef<{ id: string; resourceName: string }>()

  useInterval(() => obtainLock(resource), LOCK_REFRESH_TIME * 1000)

  useEffect(() => {
    const fn = async () => {
      lock.current = await obtainLock(resource)
    }

    fn()

    return () => {
      if (lock.current) {
        skeem.mutate2({ resourceLocks: { destroy: lock.current.id } }).catch(e => {
          if (errorIs(e, "skeem.record not found")) {
            console.warn("Couldn't clean up lock: record not found")
          } else {
            console.warn("Couldn't clean up lock", e)
          }
        })
      }
    }
  }, [resource, id, canLock])

  async function obtainLock(resource: string) {
    const earliestValidLockTime = sub(new Date(), { seconds: LOCK_VALID_FOR })
    if (!id || !canLock) {
      return
    }

    // because we go to an edit page instead of a show page like eos,
    // we first should check if it's locked before before trying to lock
    // so we can then render the locked overlay/banner etc
    const response = await skeem.fetch({
      resourceLocks: {
        filter: {
          and: [
            { matches: { resourceName: resource } },
            { matches: { resourceId: id } },
            { not: { matches: { user: { session: true } } } },
            { gt: [{ attr: "lockedAt" }, { value: earliestValidLockTime }] },
          ],
        },
      },
    })

    const [presentLock] = response

    if (presentLock) {
      setCanLock(false)
      return
    }

    try {
      if (lock.current) {
        await skeem.mutate<{ resourceName: string }>({
          resourceLocks: {
            update: {
              id: lock.current.id,
              lockedAt: new Date(),
            },
          },
        })
        return { ...lock.current, lockedAt: new Date() }
      } else {
        return await skeem.mutate<{ resourceName: string }>({
          resourceLocks: {
            create: {
              resourceName: resource,
              resourceId: id,
              user: { set: { session: true } },
            },
          },
        })
      }
    } catch (e) {
      console.warn("failed to update lock")
    }
  }

  return {
    canLock,
    reset: () => setCanLock(true),
  }
}

export type ILockData = {
  resourceId: string
  userId: string
  userName: string
  lockedAt: string
}

const lockDataCache = new Cache<Promise<ILockData[]>>({ timeoutSeconds: 5 })

export function useLockData(resource: string) {
  const skeem = useSkeem()
  const [currentLocks, setCurrentLocks] = useState<null | ILockData[]>(null)

  useInterval(() => updateLocks(resource), LOCK_CHECK_TIME * 1000)

  useEffect(() => {
    setCurrentLocks(null)
    updateLocks(resource)
  }, [resource])

  async function updateLocks(resource: string) {
    // fetching lock data for resource

    // because you could render the useLockData hooks in ultiple places (every row of the
    // index tables) we cache promise toprevent multiple similar queries
    const cached = lockDataCache.get(resource)
    if (cached) {
      const lock = await cached
      setCurrentLocks(lock)
      return
    }

    // create a promise and extract the resolve method into a variable, this means we can
    // immediately store the promise in the cache (so parallel calls will hit the cache)
    // and then we can call resolve when we want
    let resolve: (data: ILockData[]) => void = undefined as any
    const promise = new Promise<ILockData[]>(res => (resolve = res))

    lockDataCache.set(resource, promise)

    const earliestValidLockTime = sub(new Date(), { seconds: LOCK_VALID_FOR })

    // fetch all valid locks: for the given resource, not owned by us, lockedAt date is in range
    const currentLocks = await skeem.fetch<{
      resourceId: string
      user: { id: string; name: string }
      lockedAt: string
    }>({
      resourceLocks: {
        attributes: [{ name: "user", attributes: ["name"] }, "resourceId", "lockedAt"],
        filter: {
          and: [
            { matches: { resourceName: resource } },
            { not: { matches: { user: { session: true } } } },
            { gt: [{ attr: "lockedAt" }, { value: earliestValidLockTime }] },
          ],
        },
      },
    })

    const formatted = currentLocks.map(lock => ({
      resourceId: lock.resourceId,
      lockedAt: lock.lockedAt,
      userId: lock.user?.id,
      userName: lock.user?.name,
    }))

    // resolve the cached promise
    resolve(formatted)
    setCurrentLocks(formatted)
  }

  return currentLocks
}
